/**
 * 画面を「最新に保つ」間隔をサーバーに決めさせる（A151）。
 *
 * **SSE は実装されていない。** `GET /api/v1/ops/events` は
 * `{ supported: false, pollIntervalSec: 10 }` を返す（501 ではなく 200 で
 * 「対応していない」と答えるのは、SPA が機能検出してポーリングへ
 * 落とせるようにするため。apps/api/src/routes/ops/platform.ts の該当箇所に
 * 理由が書いてある）。
 *
 * そこでこのフックは **EventSource のラッパを持たない**。持たせると、
 * サーバーが一度も流さないストリームに繋ぎに行く死んだコードが残り、
 * 「リアルタイム更新がある」という誤解だけが増える。代わりに
 * `pollIntervalSec` を読んで TanStack Query の `refetchInterval` に渡す。
 *
 * こうしておくと、負荷が上がったときにサーバー側の 1 か所（この値）を
 * 伸ばすだけで全クライアントの再取得間隔を落とせる。**画面ごとに
 * 埋め込んだ定数を全部直す必要が無い。** SSE を実装する日が来たら、
 * `supported: true` を見て購読へ切り替える分岐をここに足せばよい。
 */
import { useQuery } from '@tanstack/react-query';
import { api } from './api.js';
import { qk } from './query-keys.js';
import { OPS_PERMISSION } from './permissions.js';
import { usePermission } from '../stores/auth.js';

interface EventsCapability {
  supported: boolean;
  /** ポーリング間隔（秒）。SSE 非対応時にサーバーが推奨する値。 */
  pollIntervalSec?: number;
}

/** 極端な値でブラウザを叩き続けないための下限・上限（秒）。 */
const MIN_POLL_SEC = 5;
const MAX_POLL_SEC = 300;

/**
 * 概況画面の再取得間隔（ミリ秒）を返す。
 *
 * @param fallbackMs サーバーに聞けなかったときの既定値。
 */
export function useLivePollInterval(fallbackMs: number): number {
  // `GET /ops/events` は `ops.node.read` で守られている。持たない運営者には
  // 聞きに行かない（403 を毎回踏むだけで、得られる情報は無い）。
  const canAsk = usePermission(OPS_PERMISSION.nodeRead);

  const query = useQuery({
    queryKey: qk.eventsCapability(),
    queryFn: () => api.get<EventsCapability>('/events'),
    enabled: canAsk,
    // 能力の宣言なので、開いている間に変わることはまず無い。
    staleTime: 10 * 60_000,
    // 失敗しても画面は既定値で動く。赤くしない。
    retry: false,
  });

  const sec = query.data?.pollIntervalSec;
  if (typeof sec !== 'number' || !Number.isFinite(sec)) return fallbackMs;
  return Math.min(Math.max(sec, MIN_POLL_SEC), MAX_POLL_SEC) * 1000;
}
